/**
 * Cliente HTTP unico del frontend.
 *
 * Todo servicio pasa por aca: es el unico lugar que conoce la URL base, el
 * unico que traduce un error de la API al tipo `ErrorApi` y el unico que sabe
 * que existe un token de sesion. Los componentes nunca ven un `Response` ni un
 * `fetch`.
 */
import type { RespuestaError } from '@bandu/compartido';

import type { ErrorApi } from '../tipos/api';

const URL_BASE = import.meta.env.VITE_URL_API ?? '/api';

/**
 * Token de la sesion del backoffice.
 *
 * Se guarda en memoria y en `localStorage`: en memoria para no leer el
 * almacenamiento en cada llamada, y en `localStorage` para que recargar la
 * pagina no obligue a entrar de nuevo. Es una decision consciente sobre una
 * cookie `HttpOnly`, que seria mas resistente a XSS: la tienda y el backoffice
 * son la misma SPA servida por Caddy, no hay backend de sesion propio, y una
 * cookie exigiria ademas resolver CSRF. Para tres cuentas internas el
 * intercambio es razonable; si el backoffice creciera, esta es la pieza a
 * revisar.
 */
const CLAVE_TOKEN = 'bandu.sesion';

let tokenEnMemoria: string | null = null;

function leerTokenGuardado(): string | null {
  try {
    return window.localStorage.getItem(CLAVE_TOKEN);
  } catch {
    // Navegacion privada o almacenamiento bloqueado: se trabaja sin recordar.
    return null;
  }
}

export function obtenerToken(): string | null {
  tokenEnMemoria ??= leerTokenGuardado();
  return tokenEnMemoria;
}

export function guardarToken(token: string | null): void {
  tokenEnMemoria = token;

  try {
    if (token === null) window.localStorage.removeItem(CLAVE_TOKEN);
    else window.localStorage.setItem(CLAVE_TOKEN, token);
  } catch {
    // Sin almacenamiento la sesion dura lo que dure la pestaña. No es un error.
  }
}

/**
 * Valores que pueden viajar en la query string de un listado. Un arreglo
 * repite el parametro (`?marca=A&marca=B`): unirlo con comas romperia un
 * valor que ya traiga una.
 */
export type Parametros = Record<string, string | number | boolean | readonly string[] | undefined>;

export class ErrorDeApi extends Error {
  readonly codigo: ErrorApi['codigo'];
  readonly detalles: ErrorApi['detalles'];

  constructor(error: ErrorApi) {
    super(error.mensaje);
    this.name = 'ErrorDeApi';
    this.codigo = error.codigo;
    this.detalles = error.detalles;
  }
}

function esRespuestaError(valor: unknown): valor is RespuestaError {
  return typeof valor === 'object' && valor !== null && 'codigo' in valor && 'mensaje' in valor;
}

/** Descarta los parametros vacios para no ensuciar la URL con `?marca=`. */
function consulta(parametros: Parametros | undefined): string {
  if (parametros === undefined) return '';

  const query = new URLSearchParams();
  for (const [clave, valor] of Object.entries(parametros)) {
    if (valor === undefined || valor === '') continue;
    if (Array.isArray(valor)) {
      for (const elemento of valor) query.append(clave, elemento);
    } else {
      query.set(clave, String(valor));
    }
  }

  const texto = query.toString();
  return texto.length > 0 ? `?${texto}` : '';
}

function cabeceras(tipoContenido = 'application/json'): HeadersInit {
  const token = obtenerToken();

  return {
    'Content-Type': tipoContenido,
    ...(token !== null ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface OpcionesPeticion {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  parametros?: Parametros;
  cuerpo?: unknown;
  /**
   * Cuerpo binario, que reemplaza a `cuerpo` y manda el tipo del propio blob en
   * vez de JSON. Lo usa la busqueda por foto: la imagen viaja como bytes
   * crudos, sin `multipart` y sin envolverla en un JSON que la haria 33% mas
   * pesada al pasarla a base64.
   */
  cuerpoBinario?: Blob;
  senal?: AbortSignal;
}

export async function pedir<T>(ruta: string, opciones: OpcionesPeticion = {}): Promise<T> {
  const binario = opciones.cuerpoBinario;

  const respuesta = await fetch(`${URL_BASE}${ruta}${consulta(opciones.parametros)}`, {
    method: opciones.metodo ?? 'GET',
    headers: binario !== undefined ? cabeceras(binario.type) : cabeceras(),
    ...(binario !== undefined ? { body: binario } : {}),
    ...(binario === undefined && opciones.cuerpo !== undefined
      ? { body: JSON.stringify(opciones.cuerpo) }
      : {}),
    ...(opciones.senal ? { signal: opciones.senal } : {}),
  });

  // 204 no trae cuerpo: leerlo como JSON reventaria.
  const cuerpo: unknown = respuesta.status === 204 ? null : await respuesta.json().catch(() => null);

  if (!respuesta.ok) {
    if (esRespuestaError(cuerpo)) {
      throw new ErrorDeApi({
        codigo: cuerpo.codigo,
        mensaje: cuerpo.mensaje,
        ...(cuerpo.detalles !== undefined ? { detalles: cuerpo.detalles } : {}),
      });
    }
    throw new ErrorDeApi({
      codigo: 'error_interno',
      mensaje: 'No pudimos conectarnos con el servidor.',
    });
  }

  return cuerpo as T;
}

/**
 * Descarga de un archivo que arma el servidor (el PDF de una cotizacion). Va
 * con el token igual que `pedir`; por eso no basta un `<a href>`.
 */
export async function pedirArchivo(ruta: string): Promise<Blob> {
  const respuesta = await fetch(`${URL_BASE}${ruta}`, { headers: cabeceras() });

  if (!respuesta.ok) {
    const cuerpo: unknown = await respuesta.json().catch(() => null);
    throw new ErrorDeApi(
      esRespuestaError(cuerpo)
        ? { codigo: cuerpo.codigo, mensaje: cuerpo.mensaje }
        : { codigo: 'error_interno', mensaje: 'No pudimos conectarnos con el servidor.' },
    );
  }

  return respuesta.blob();
}

/**
 * Llamada que no espera respuesta util (un registro de metrica, por ejemplo).
 * No propaga el error: que no se anote un clic no puede romper la navegacion.
 */
export function avisar(ruta: string, cuerpo: unknown): void {
  void pedir(ruta, { metodo: 'POST', cuerpo }).catch(() => undefined);
}
